import {useEffect, useMemo, useState} from 'react';

type Serializer<T> = (object: T | undefined) => string;
type Parser<T> = (val: string) => T | undefined;
type Setter<T> = React.Dispatch<React.SetStateAction<T>>;

type Options<T> = Partial<{
  serializer: Serializer<T>;
  parser: Parser<T>;
  logger: (error: any) => void;
}>;

function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: Options<T>,
): [T, Setter<T>];
function useLocalStorage<T>(
  key: string,
  defaultValue?: undefined,
  options?: Options<T>,
): [T | undefined, Setter<T | undefined>];
function useLocalStorage<T>(
  key: string,
  defaultValue?: T,
  options?: Options<T>,
) {
  const opts = useMemo(() => {
    return {
      serializer: JSON.stringify,
      parser: JSON.parse,
      logger: console.log,
      ...options,
    };
  }, [options]);

  const {serializer, parser, logger} = opts;
  const getCurrentState = (key: string) => {
    const item = window.localStorage.getItem(key);
    try {
      const res: T = item ? parser(item) : defaultValue;
      return res;
    } catch (e) {
      logger(`${e}, useLocalStorage set ${key} default value as ${item || defaultValue}`);
      return item || defaultValue;
    }
  }

  const [storedValue, setValue] = useState(() => {
    return getCurrentState(key)
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, serializer(storedValue));
      window.dispatchEvent(new StorageEvent('storage', { key, newValue: JSON.stringify(storedValue) }));
    } catch (e) {
      logger(e);
    }
  }, [storedValue]);

  useEffect(() => { // fix useLocalStorage 與 usePageData 內 localStorage的資料不會同步
    const onReceieveMessage = (e: any) => {
      if (e.key === key && e.newValue !== JSON.stringify(storedValue)) {
        setValue(JSON.parse(e.newValue))
      }
    }
    window.addEventListener('storage', onReceieveMessage)
    return () => window.removeEventListener('storage', onReceieveMessage)
  }, [storedValue, setValue])

  return [storedValue, setValue];
}

export default useLocalStorage;
